/**
 * Supplementary grammar rules for common mistakes harper.js does not flag
 * (e.g. "The patient have", "The vials was", "I seen", "Your welcome").
 *
 * Rules are deliberately narrow, pattern based checks to avoid false positives.
 * They produce lints in the same shape as `harperService.lintText`.
 */
import { SUGGESTION_KIND } from './utils';

// Word lists are written as space separated text to keep them short:
// words('a b') -> ['a', 'b'], pairs('a:b c:d') -> { a: 'b', c: 'd' }
const words = list => list.trim().split(/\s+/);
const pairs = list =>
  words(list).reduce((map, pair) => {
    const [key, value] = pair.split(':');
    return { ...map, [key]: value };
  }, {});

const SINGULAR_SUBJECT_NOUNS = words(`
  patient product device box bottle vial tablet capsule pen syringe injector
  package carton seal cap label lot batch sample reporter caller consumer
  customer nurse physician doctor pharmacist complaint medication medicine
  dose kit needle cartridge hospital clinic pharmacy surgeon caregiver
  ambulance department ward record report result treatment procedure operation
  sister brother mother father son daughter wife husband baby friend
`);

// Simple plurals of the nouns above, e.g. "vials", "boxes"
const PLURAL_SUBJECT_NOUNS = SINGULAR_SUBJECT_NOUNS.map(noun => {
  if (/(x|s|ch|sh)$/.test(noun)) return `${noun}es`;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  return `${noun}s`;
});

const DETERMINERS = 'the|this|that|my|his|her|their|our|your|each|every';
// One optional word between the determiner and the noun, e.g. "The medical records was".
// It can't be another determiner, so "that the patient take" is still seen as starting at "the".
const MODIFIER =
  '(?:(?!(?:the|a|an|this|that|these|those|my|his|her|their|our|your|each|every|both|all)\\s)[a-z]+\\s+)?';
const PLURAL_DETERMINERS = 'the|these|those|my|his|her|their|our|your|both|all';

// Singular subject followed by a plural verb form
const SINGULAR_VERB_FIXES = { have: 'has', were: 'was', are: 'is', do: 'does' };
// Plural subject followed by a singular verb form
const PLURAL_VERB_FIXES = { has: 'have', was: 'were', is: 'are', does: 'do' };

const AGREEMENT_MESSAGE = 'The verb should agree in number with its subject.';

// Words before "the patient have" etc. that make the base verb form correct:
// "Did the patient have pain?", "If the patient were", "The doctor and the nurse have",
// "One of the vials is", "Two doses of the medicine are"
const AGREEMENT_EXCEPTIONS = /\b(?:did|does|do|will|would|can|could|should|shall|may|might|must|and|or|nor|if|of)\s+$/i;

// Words after "there is/was" that make the subject plural, e.g. "There is many nurses".
// Digits are left out on purpose: "There is 5 mg remaining" is correct.
const PLURAL_QUANTIFIERS = words(`
  many several few two three four five six seven eight nine ten
`);
const THERE_VERB_FIXES = { is: 'are', was: 'were' };

// Verbs commonly followed by an object and a manner adverb, e.g. "examined the patient carefully"
const MANNER_VERBS = words(`
  examine check inspect handle clean review monitor treat administer follow
  explain describe record document measure pack label store perform
`);
const MANNER_VERB_FORMS = MANNER_VERBS.map(verb => {
  const stem = verb.replace(/e$/, '');
  return `${verb}|${verb}s|${stem}ed|${stem}ing`;
}).join('|');

// Adjectives misused in place of the adverb, e.g. "careful" for "carefully"
const ADVERB_FIXES = pairs(`
  careful:carefully careless:carelessly quick:quickly slow:slowly
  proper:properly improper:improperly correct:correctly incorrect:incorrectly
  thorough:thoroughly immediate:immediately regular:regularly gentle:gently
  frequent:frequently accurate:accurately prompt:promptly clear:clearly
  bad:badly
`);

// The adjective must end the clause, so "reviewed the patient regular medications" is not flagged.
// "and" + another adjective is a describing pair: "explained the procedure clear and simple" is correct.
const CLAUSE_END =
  '(?=\\s*(?:[.,;:!?]|$)|\\s*\\n|\\s+(?:and(?!\\s+(?:simple|easy|concise|precise|direct|calm|complete|accurate|clear|short|brief|detailed|thorough|gentle|careful)\\b)|but|then|before|after|while|because|to|for|in|on|at|with|by|during|again)\\b)';

// Verbs whose base form is rarely a noun, so "The hospital provide" can be flagged safely.
// Words like "report", "visit", "use" or "need" are left out: "The patient visit was..." is correct.
const BASE_VERBS = words(`
  provide give take go come seem feel know think say tell want require receive
  contain become bring keep get understand believe remember forget agree
  refuse deny explain describe complain suffer prefer live speak write wear
  arrive develop mention decide continue appear happen include allow ask try
  perform prescribe recommend administer examine inject apply carry accept
  remain
`);

const toThirdPerson = verb => {
  if (verb === 'go') return 'goes';
  if (/(s|x|z|ch|sh|o)$/.test(verb)) return `${verb}es`;
  if (/[^aeiou]y$/.test(verb)) return `${verb.slice(0, -1)}ies`;
  return `${verb}s`;
};

const IRREGULAR_PAST = pairs(`
  give:gave take:took go:went come:came feel:felt know:knew think:thought
  say:said tell:told become:became bring:brought keep:kept get:got
  understand:understood forget:forgot speak:spoke write:wrote wear:wore
`);

const toPast = verb => {
  if (IRREGULAR_PAST[verb]) return IRREGULAR_PAST[verb];
  if (/e$/.test(verb)) return `${verb}d`;
  if (/[^aeiou]y$/.test(verb)) return `${verb.slice(0, -1)}ied`;
  if (/^(?:prefer|refer|admit)$/.test(verb))
    return `${verb}${verb.slice(-1)}ed`;
  return `${verb}ed`;
};

// Past time words in the same sentence: "The ambulance arrive late last night" -> "arrived"
const PAST_TIME = /\b(?:yesterday|ago|last\s+(?:night|week|month|year|time|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|earlier\s+today|this\s+morning)\b/i;
const sentenceAfter = (text, index) => text.slice(index).split(/[.!?\n]/)[0];

// "provide" -> "provides"
const THIRD_PERSON_FIXES = BASE_VERBS.reduce(
  (fixes, verb) => ({ ...fixes, [verb]: toThirdPerson(verb) }),
  {}
);
// "takes" -> "take", "has" -> "have"
const BASE_FORM_FIXES = BASE_VERBS.reduce(
  (fixes, verb) => ({ ...fixes, [toThirdPerson(verb)]: verb }),
  { has: 'have' }
);

// Words before "the nurse give" that make the base form correct: "Did the nurse give...",
// "Let the nurse give...", "Watched the nurse give...", "recommended that the patient take..."
const BASE_VERB_EXCEPTIONS = /\b(?:did|does|do|will|would|can|could|should|shall|may|might|must|and|or|nor|if|of|let|lets|make|makes|made|help|helps|helped|have|has|had|see|saw|watch|watched|hear|heard|observed|noticed|that)\s+$/i;

// Apostrophes may be straight (') or curly (’), e.g. text pasted from Word or email
const MODALS =
  "can|could|will|would|shall|should|may|might|must|cannot|can['’]t|won['’]t|wouldn['’]t|shouldn['’]t|couldn['’]t|doesn['’]t|didn['’]t|don['’]t";

// "The can contains..." is a noun, not a modal
const DETERMINER_BEFORE = /\b(?:the|a|an|this|that|each|every|my|his|her|its|our|their|your)\s+$/i;

// Base forms used where a past participle is needed: "was build" -> "built"
const PARTICIPLE_FIXES = pairs(`
  build:built make:made give:given take:taken write:written bring:brought
  buy:bought send:sent tell:told break:broken choose:chosen keep:kept
  leave:left lose:lost sell:sold teach:taught throw:thrown steal:stolen
  speak:spoken spend:spent forget:forgotten eat:eaten hide:hidden know:known
  prescribe:prescribed administer:administered inject:injected
  diagnose:diagnosed examine:examined deliver:delivered admit:admitted
  replace:replaced receive:received hospitalize:hospitalized notify:notified
  inform:informed advise:advised instruct:instructed refer:referred
`);

// Subject pronouns used after a preposition: "to she" -> "to her"
const OBJECT_PRONOUNS = pairs(`
  i:me he:him she:her we:us they:them
`);
const PREPOSITIONS =
  'to|for|with|from|about|of|by|at|toward|towards|without|against|between|among|behind|beside|near|upon';

// Places that need an article in American English: "visited hospital" -> "the hospital"
const ARTICLE_PLACES = 'hospital|clinic|pharmacy|emergency room';
const PLACE_VERBS =
  'visit|visits|visited|visiting|go to|goes to|went to|going to|gone to|come to|comes to|came to|coming to|return to|returns to|returned to|arrive at|arrives at|arrived at';
// The place must end the phrase, so "visited hospital staff" is not flagged
const PLACE_END =
  '(?=\\s*(?:[.,;:!?]|$)|\\s*\\n|\\s+(?:yesterday|today|tonight|tomorrow|last|this|every|again|twice|once|recently|earlier|later|and|but|or|then|before|after|because|for|on|in|at|with|by|during)\\b)';

// Verbs that are also common nouns ("a check", "a need"), only flagged when followed by an
// object: "The nurse check the temperature", "The patient need to rest"
const NOUN_LIKE_VERBS = 'check|use|help';
const OBJECT_START =
  'the|a|an|his|her|their|my|our|your|its|him|them|it|me|us|this|that|these|those';

// "Please gives the patient water" -> "give"
const PLEASE_FIXES = [
  ...BASE_VERBS,
  ...words(`
    check use help need make call send inform contact ensure confirm follow
    review note wait stop start return sign fill complete submit update add
    remove attach verify
  `)
].reduce((fixes, verb) => ({ ...fixes, [toThirdPerson(verb)]: verb }), {});

// "must to" is fine when "will" is a noun: "the will to live"
const MODAL_TO_EXCEPTIONS = /\b(?:the|a|an|this|that|his|her|its|our|their|your|my|free|own|good|ill|strong)\s+$/i;

const WEEKDAYS = 'monday|tuesday|wednesday|thursday|friday|saturday|sunday';
const MONTHS =
  'january|february|march|april|may|june|july|august|september|october|november|december';

// "since" followed by a point in time, so "since it was closed" (= because) is not flagged
const SINCE_TIME = `since\\s+(?:yesterday|last|then|birth|childhood|\\d|(?:this|that)\\s+(?:morning|afternoon|evening|week|month|year)|${WEEKDAYS}|${MONTHS}|the\\s+(?:morning|weekend|accident|surgery|operation|procedure|injection|dose|start|beginning|first))\\b`;

// People words, so "Salt and pepper is" is not treated as two people
const PEOPLE_NOUNS = words(`
  brother sister mother father mom dad son daughter wife husband friend
  friends parents child children baby family colleague neighbor boyfriend
  girlfriend partner cousin uncle aunt grandmother grandfather doctor nurse
  patient pharmacist physician surgeon caregiver reporter caller consumer
  customer
`).join('|');
// One person in a compound subject: "my brother", "the nurse", "doctor", "I", "he".
// "the" is optional: "The doctor and patient is", "Doctor and patient is"
const PERSON = `(?:(?:(?:the|my|his|her|their|our|your|a|an)\\s+)?(?:${PEOPLE_NOUNS})|i|he|she|you|we|they|me|him|her|us|them)`;
// Verbs that can follow a subject, so "He saw me and my brother" (object) is not flagged
const SUBJECT_VERB =
  'is|are|was|were|am|has|have|had|will|would|can|could|should|went|go|goes|came|come|did|do|does|took|saw|said|told|got|left|arrived|visited';
const SUBJECT_PRONOUNS = pairs(`
  me:I him:he her:she us:we them:they
`);

const matchCase = (source, replacement) =>
  source[0] === source[0].toUpperCase()
    ? replacement[0].toUpperCase() + replacement.slice(1)
    : replacement;

const RULES = [
  {
    // "me and my brother is going" -> "My brother and I". Start of a sentence only, and only
    // when a verb follows, so "He saw me and my brother" is not flagged
    kind: 'Grammar',
    pattern: new RegExp(
      `(?:^|[.!?]\\s+|\\n\\s*)((?:me|him|her|us|them)\\s+and\\s+${PERSON}|${PERSON}\\s+and\\s+(?:me|him|her|us|them))(?=\\s+(?:${SUBJECT_VERB})\\b)`,
      'gi'
    ),
    message:
      'Use "I", "he", "she", "we" or "they" as the subject, and mention yourself last ("My brother and I").',
    // The fix also removes extra spaces, so it replaces harper's "2 spaces" lint inside it
    preferOverHarper: true,
    replace: word => {
      const [first, second] = word.split(/\s+and\s+/i);
      const fix = part => SUBJECT_PRONOUNS[part.toLowerCase()] || part;
      // Put "I" last; keep other names in their original order
      const parts =
        first.toLowerCase() === 'me'
          ? [second, 'I']
          : [fix(first), fix(second)];
      const sentence = `${parts[0]} and ${parts[1]}`;
      return sentence[0].toUpperCase() + sentence.slice(1);
    }
  },
  {
    // "me and my brother is going" -> "are". Two people joined by "and" take a plural verb
    // (harper wrongly suggests "am" after "my brother and I")
    kind: 'Agreement',
    pattern: new RegExp(
      `(?:^|[.!?]\\s+|\\n\\s*)${PERSON}\\s+and\\s+${PERSON}\\s+(is|was|has)\\b`,
      'gi'
    ),
    message: 'Two people joined by "and" take a plural verb.',
    preferOverHarper: true,
    wordAtEnd: true,
    replace: word =>
      ({ is: 'are', was: 'were', has: 'have' }[word.toLowerCase()])
  },
  {
    // Capital letter at the very start of the text (harper only checks after a full stop)
    kind: 'Capitalization',
    // Whole lowercase word only, so "iPhone" or "eGFR" is not flagged. Skips "me and my brother ...",
    // which the rule above already fixes as "My brother and I"
    pattern: /^\s*(?!(?:me|him|her|us|them)\s+and\s)([a-z][a-z']*)(?![A-Za-z0-9])/g,
    message: 'This sentence does not start with a capital letter.',
    replace: word => word[0].toUpperCase() + word.slice(1)
  },
  {
    // "The patient have pain" -> "has"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\b(?:${DETERMINERS})\\s+${MODIFIER}(?:${SINGULAR_SUBJECT_NOUNS.join(
        '|'
      )})\\s+(have|were|are|do)\\b`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    skipAfter: AGREEMENT_EXCEPTIONS,
    replace: word => SINGULAR_VERB_FIXES[word.toLowerCase()]
  },
  {
    // "The vials was cracked" -> "were"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\b(?:${PLURAL_DETERMINERS})\\s+${MODIFIER}(?:${PLURAL_SUBJECT_NOUNS.join(
        '|'
      )})\\s+(has|was|is|does)\\b`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    skipAfter: AGREEMENT_EXCEPTIONS,
    replace: word => PLURAL_VERB_FIXES[word.toLowerCase()]
  },
  {
    // "There is many nurses" -> "are", "There was two vials" -> "were"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\bthere\\s+(is|was)\\s+(?:(?:${PLURAL_QUANTIFIERS.join(
        '|'
      )})\\b|(?:${PLURAL_SUBJECT_NOUNS.join('|')})\\b)`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    replace: word => THERE_VERB_FIXES[word.toLowerCase()]
  },
  {
    // "examined the patient careful" -> "carefully"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b(?:${MANNER_VERB_FORMS})\\s+(?:(?:the|a|an|his|her|their|my|our|your|this|that|these|those|each|every)\\s+[a-z]+|it|him|them)\\s+(${Object.keys(
        ADVERB_FIXES
      ).join('|')})${CLAUSE_END}`,
      'gi'
    ),
    message: 'Use an adverb to describe how the action was done.',
    wordAtEnd: true,
    replace: word => ADVERB_FIXES[word.toLowerCase()]
  },
  {
    // "The hospital provide excellent care" -> "provides"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\b(?:${DETERMINERS})\\s+${MODIFIER}(?:${SINGULAR_SUBJECT_NOUNS.join(
        '|'
      )})\\s+(${BASE_VERBS.join('|')})\\b`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    skipAfter: BASE_VERB_EXCEPTIONS,
    replace: (word, match, text, index) =>
      PAST_TIME.test(sentenceAfter(text, index))
        ? toPast(word.toLowerCase())
        : THIRD_PERSON_FIXES[word.toLowerCase()]
  },
  {
    // "Patients should takes their medication" -> "take"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b(?:${MODALS})\\s+(?:not\\s+)?(${Object.keys(BASE_FORM_FIXES).join(
        '|'
      )})\\b`,
      'gi'
    ),
    message: 'Use the base form of the verb after "can", "should", "will" etc.',
    skipAfter: DETERMINER_BEFORE,
    wordAtEnd: true,
    replace: word => BASE_FORM_FIXES[word.toLowerCase()]
  },
  {
    // "The hospital was build ten years ago" -> "built"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b(?:is|are|was|were|be|been|being|has been|have been|had been)\\s+(?:not\\s+)?(?:(?:already|just|recently|never|also|then)\\s+)?(${Object.keys(
        PARTICIPLE_FIXES
      ).join('|')})\\b`,
      'gi'
    ),
    message: 'Use the past participle after "was", "is", "been" etc.',
    wordAtEnd: true,
    replace: word => PARTICIPLE_FIXES[word.toLowerCase()]
  },
  {
    // "explained the treatment to she" -> "her"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b(?:${PREPOSITIONS})\\s+(he|she|we|they|i)\\b(?!\\s*(?:'|’)\\w)(?=\\s*(?:[.,;:!?)]|$)|\\s+(?:and|or|but|yesterday|today|tomorrow|again|too|also|about|before|after|for|at|in|on|with)\\b)`,
      'gi'
    ),
    message: 'Use an object pronoun ("her", "him", "them", "us", "me") here.',
    wordAtEnd: true,
    replace: word => OBJECT_PRONOUNS[word.toLowerCase()]
  },
  {
    // "I visited hospital yesterday" -> "the hospital"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b(?:${PLACE_VERBS})\\s+(${ARTICLE_PLACES})${PLACE_END}`,
      'gi'
    ),
    message: 'Add an article ("the" or "a") before the place.',
    wordAtEnd: true,
    replace: word => `the ${word}`
  },
  {
    // "The nurse check the temperature" -> "checks", "The patient need to rest" -> "needs"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\b(?:${DETERMINERS})\\s+${MODIFIER}(?:${SINGULAR_SUBJECT_NOUNS.join(
        '|'
      )})\\s+(?:(${NOUN_LIKE_VERBS})(?=\\s+(?:${OBJECT_START})\\b)|(need)(?=\\s+(?:to|${OBJECT_START})\\b))`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    skipAfter: BASE_VERB_EXCEPTIONS,
    wordAtEnd: true,
    replace: word => toThirdPerson(word.toLowerCase())
  },
  {
    // "She don't feel well" -> "doesn't", "He do not" -> "does"
    kind: 'Agreement',
    pattern: /\b(?:he|she|it)\s+(?:(don['’]t)\b|(do)(?=\s+not\b))/gi,
    message: AGREEMENT_MESSAGE,
    replace: word => (/^do$/i.test(word) ? 'does' : `doesn${word.slice(3)}`)
  },
  {
    // "The patient don't" -> "doesn't"
    kind: 'Agreement',
    pattern: new RegExp(
      `\\b(?:${DETERMINERS})\\s+${MODIFIER}(?:${SINGULAR_SUBJECT_NOUNS.join(
        '|'
      )})\\s+(don['’]t)\\b`,
      'gi'
    ),
    message: AGREEMENT_MESSAGE,
    skipAfter: AGREEMENT_EXCEPTIONS,
    wordAtEnd: true,
    replace: word => `doesn${word.slice(3)}`
  },
  {
    // "Patients must to wear" -> "must wear"
    kind: 'Grammar',
    pattern: /\b((?:must|should|can|could|would|shall)\s+to)(?=\s+[a-z])/gi,
    message: 'Do not use "to" after "must", "should", "can" etc.',
    skipAfter: MODAL_TO_EXCEPTIONS,
    replace: word => word.split(/\s+/)[0]
  },
  {
    // "Please gives the patient water" -> "give"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\bplease\\s+(${Object.keys(PLEASE_FIXES).join('|')})\\b`,
      'gi'
    ),
    message: 'Use the base form of the verb after "please".',
    wordAtEnd: true,
    replace: word => PLEASE_FIXES[word.toLowerCase()]
  },
  {
    // "works very hardly" -> "hard" ("hardly" means "almost not")
    kind: 'WordChoice',
    pattern: /\b(?:work|works|worked|working|try|tries|tried|trying|push|pushes|pushed|pushing|train|trains|trained|training|study|studies|studied|studying)\s+(?:(?:very|really|so|too|extremely|quite)\s+)?(hardly)\b(?!\s+(?:any|anything|anyone|anybody|ever|at\s+all|a|enough)\b)/gi,
    message: '"Hardly" means "almost not". Use "hard" to mean "with effort".',
    wordAtEnd: true,
    replace: () => 'hard'
  },
  {
    // "an appointment in Monday" -> "on Monday" (not "at Monday's meeting")
    kind: 'WordChoice',
    pattern: new RegExp(`\\b(in|at)\\s+(?:${WEEKDAYS})s?\\b(?!['’])`, 'gi'),
    message: 'Use "on" with days of the week.',
    replace: () => 'on'
  },
  {
    // "He is suffering ... since yesterday" -> "has been suffering"
    kind: 'Grammar',
    pattern: new RegExp(
      `\\b((?:is|are|am)\\s+(?:not\\s+)?(?!(?:no|some|any|every)?thing\\b|morning\\b|evening\\b|during\\b|being\\b)[a-z]+ing)\\b(?=[^.!?;\\n]*\\b${SINCE_TIME})`,
      'gi'
    ),
    message:
      'Use "has been" / "have been" + "-ing" for something that started in the past and is still going on.',
    replace: word =>
      word.replace(
        /^(is|are|am)(\s+not)?/i,
        (m, aux, neg) =>
          `${aux.toLowerCase() === 'is' ? 'has' : 'have'}${neg || ''} been`
      )
  },
  {
    // "We is" / "They is" -> "are", "I is" -> "am" (harper flags these without a suggestion)
    kind: 'Agreement',
    pattern: /\b(?:(?:we|they|you)\s+(is)|i\s+(is|are))\b/gi,
    message: AGREEMENT_MESSAGE,
    // "My brother and I are" is correct
    skipAfter: /\band\s+$/i,
    replace: (word, match) => (/^i\s/i.test(match) ? 'am' : 'are')
  },
  {
    // "I seen it" -> "saw" / "have seen". Nouns are left out: "Patient seen in ER" is normal note style
    kind: 'Grammar',
    pattern: /\b(?:i|we|they|you|he|she)\s+(seen|done)\b/gi,
    message:
      'Use the past tense, or add "have"/"has" before the past participle.',
    replace: word => (word.toLowerCase() === 'seen' ? 'saw' : 'did')
  },
  {
    // "Your welcome" -> "You're welcome"
    kind: 'WordChoice',
    pattern: /\b(your)\s+welcome\b/gi,
    message: 'Did you mean "you\'re" (you are)?',
    replace: () => "you're"
  },
  {
    // "effected the patient" -> "affected"
    kind: 'WordChoice',
    pattern: /\b(effected|effecting)\s+(?:the|a|an|his|her|their|my|our|your|him|them|me|us)\b/gi,
    message:
      '"Affect" is usually the verb ("to influence"); "effect" is usually the noun.',
    replace: word =>
      word.toLowerCase() === 'effected' ? 'affected' : 'affecting'
  }
];

/**
 * @returns {Array} lints with UTF-16 `start`/`end` offsets
 */
export const lintWithCustomRules = text =>
  RULES.reduce((lints, rule) => {
    const pattern = new RegExp(rule.pattern); // fresh copy: global regexes are stateful
    let match = pattern.exec(text);
    while (match) {
      if (rule.skipAfter && rule.skipAfter.test(text.slice(0, match.index))) {
        match = pattern.exec(text);
        continue; // eslint-disable-line no-continue
      }
      const groupIndex = match.findIndex((g, i) => i > 0 && g !== undefined);
      const word = match[groupIndex];
      // `wordAtEnd`: the word may also appear earlier in the match, e.g. "checked the correctness correct"
      const offset = rule.wordAtEnd
        ? match[0].lastIndexOf(word)
        : match[0].indexOf(word);
      const start = match.index + offset;
      const replacement = rule.replace(word, match[0], text, start);
      lints.push({
        start,
        end: start + word.length,
        kind: rule.kind,
        message: rule.message,
        preferOverHarper: rule.preferOverHarper,
        problemText: word,
        suggestions: [
          {
            kind: SUGGESTION_KIND.REPLACE,
            text: matchCase(word, replacement)
          }
        ]
      });
      match = pattern.exec(text);
    }
    return lints;
  }, []);

/**
 * Merges harper lints with custom rule lints. Where both flag the same range,
 * harper's lint is kept but given the custom suggestion if it has none.
 */
export const mergeLints = (harperLints, allCustomLints) => {
  // Some custom rules replace harper's lint on the same word, e.g. "my brother and I is" -> "are", not "am"
  const preferred = allCustomLints.filter(c => c.preferOverHarper);
  const customLints = allCustomLints.filter(c => !c.preferOverHarper);
  const merged = harperLints
    .filter(
      lint => !preferred.some(c => c.start < lint.end && lint.start < c.end)
    )
    .concat(preferred)
    .map(lint => {
      if (lint.suggestions.length) {
        return lint;
      }
      const custom = customLints.find(
        c => c.start === lint.start && c.end === lint.end
      );
      return custom ? { ...lint, suggestions: custom.suggestions } : lint;
    });

  customLints.forEach(custom => {
    const overlaps = merged.some(
      lint => custom.start < lint.end && lint.start < custom.end
    );
    if (!overlaps) {
      merged.push(custom);
    }
  });

  return merged;
};
